/**
 * Admin Routing Inspector
 * 
 * The audit log of record for all routing decisions.
 * Reads exclusively from database (leads + routing_attempts).
 * 
 * Single Source of Truth: Database is authoritative.
 * RoutingEventStore is DEV-ONLY session data (collapsible debug panel).
 */

import { useState, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { useQueryClient } from "@tanstack/react-query";
import { routingLogsQueryKeys } from "@/hooks/useRoutingLogsDatabase";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { ReviewActionsPanel } from "@/components/admin/ReviewActionsPanel";
import { useLeads, useLeadStats, leadsQueryKeys } from "@/hooks/useLeadsDatabase";
import { useRoutingAttempts, useRoutingAttemptStats, routingAttemptsQueryKeys } from "@/hooks/useRoutingAttempts";
import { useRoutingEventStats, useRoutingEventActions } from "@/hooks/useRoutingEvents";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { 
  Eye,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Package,
  Database,
  History,
  ChevronDown,
  ChevronRight,
  User,
  DollarSign,
  Activity,
  Trash2,
  Zap,
  Bug,
  Loader2,
} from "lucide-react";
import { getStatusLabel, getStatusBadgeVariant, type LeadStatus, type LifecycleLead } from "@/lib/leadStateMachine";
import { isDevInjectionAvailable } from "@/lib/dev/eligibilityInjection";
import { routeRetryLead } from "@/lib/routingOrchestrator";
import { useMockAgents } from "@/contexts/MockAgentsContext";
import { useAllocationWeights } from "@/contexts/AllocationWeightsContext";
import { usePricingRules } from "@/contexts/PricingRulesContext";
import { useSystemControl } from "@/contexts/SystemControlContext";
import { toast } from "sonner";
import { DateRangeFilter, type DateRangeValue, type PresetKey } from "@/components/DateRangeFilter";

export default function AdminRoutingInspector() {
  const queryClient = useQueryClient();
  
  // Database sources (authoritative)
  const { data: leads, isLoading: leadsLoading } = useLeads();
  const { data: leadStats } = useLeadStats();
  const { data: attempts, isLoading: attemptsLoading } = useRoutingAttempts(100);
  const { data: attemptStats } = useRoutingAttemptStats();
  
  // Session-based (debug only)
  const eventStats = useRoutingEventStats();
  const eventActions = useRoutingEventActions();
  
  // Routing context
  const { clientAgents } = useMockAgents();
  const { actions: weightsActions } = useAllocationWeights();
  const { actions: pricingActions } = usePricingRules();
  const { actions: systemActions } = useSystemControl();
  
  // UI State
  const [statusFilter, setStatusFilter] = useState<"all" | LeadStatus>("all");
  const [sourceFilter, setSourceFilter] = useState<"all" | "LIVE" | "RETRY">("all");
  const [inspectorSearch, setInspectorSearch] = useState("");
  const [dateRange, setDateRange] = useState<DateRangeValue | undefined>(undefined);
  const [datePreset, setDatePreset] = useState<PresetKey | undefined>(undefined);
  const [expandedLeads, setExpandedLeads] = useState<Set<string>>(new Set());
  const [debugPanelOpen, setDebugPanelOpen] = useState(false);
  const [retryingLeadId, setRetryingLeadId] = useState<string | null>(null);
  
  const isLoading = leadsLoading || attemptsLoading;
  // Use combined check: either global OR per-agent overrides enable DEV injection
  const devInjectionAvailable = isDevInjectionAvailable();
  
  
  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: leadsQueryKeys.all });
    queryClient.invalidateQueries({ queryKey: routingAttemptsQueryKeys.all });
    queryClient.invalidateQueries({ queryKey: routingLogsQueryKeys.all });
  };
  
  // Force retry a lead immediately (DEV only)
  // CRITICAL: Uses routeRetryLead() to ensure fresh DEV override evaluation
  const handleForceRetry = useCallback(async (lead: LifecycleLead) => {
    if (!devInjectionAvailable || lead.status !== 'RETRY_QUEUED') return;
    
    setRetryingLeadId(lead.id);
    try {
      const weights = weightsActions.getWeights();
      
      // Use routeRetryLead() - the ONLY valid entry point for retry routing
      // This ensures:
      // 1. Old routingContext is cleared (forces fresh evaluation)
      // 2. DEV overrides are re-evaluated from CURRENT session state
      // 3. Full normalization pipeline runs from scratch
      const result = routeRetryLead(lead, clientAgents, weights, {
        getPricingRule: (leadTypeId, state) => {
          const rule = pricingActions.getPricingRuleForLead(leadTypeId, state);
          return rule ? { basePrice: rule.basePrice, active: rule.active } : null;
        },
        checkIntake: (vertical) => systemActions.getIntakeRejectionReason(vertical),
        targetPoolSize: 8,
        debug: true,
      });
      
      // CRITICAL: Await persistence before showing results and refreshing
      if (result.persistence) {
        try {
          await result.persistence;
        } catch (persistErr) {
          console.error('[Inspector] Persistence failed:', persistErr);
          // Continue showing result even if persistence failed
        }
      }
      
      if (result.success) {
        toast.success('Lead routed successfully', {
          description: `Routed to ${result.deliveryImpact.agentName} for $${result.deliveryImpact.chargedAmount}`,
        });
      } else if (result.outcome === 'RETRY_QUEUED') {
        toast.info('Still no eligible agents', {
          description: result.observability.rejectionReason || 'Lead re-queued for retry.',
        });
      } else {
        toast.warning('Lead stocked', {
          description: 'Retries exhausted. Lead moved to stock.',
        });
      }
      
      handleRefresh();
    } catch (error) {
      console.error('[Inspector] Force retry failed:', error);
      toast.error('Force retry failed', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setRetryingLeadId(null);
    }
  }, [clientAgents, weightsActions, pricingActions, systemActions, devInjectionAvailable]);
  
  // Group attempts by lead ID and sort by attempt number descending (latest first)
  const attemptsByLead = useMemo(() => {
    if (!attempts) return new Map<string, typeof attempts>();
    const map = new Map<string, typeof attempts>();
    for (const attempt of attempts) {
      const existing = map.get(attempt.leadId) || [];
      existing.push(attempt);
      map.set(attempt.leadId, existing);
    }
    // Sort each group by attempt number descending (latest first)
    for (const [_leadId, leadAttempts] of map) {
      leadAttempts.sort((a, b) => b.attemptNumber - a.attemptNumber);
    }
    return map;
  }, [attempts]);
  
  // Filter leads
  const filteredLeads = useMemo(() => {
    if (!leads) return [];
    return leads.filter(lead => {
      if (statusFilter !== "all" && lead.status !== statusFilter) return false;
      if (sourceFilter !== "all") {
        const leadAttempts = attemptsByLead.get(lead.id) || [];
        const hasMatchingSource = leadAttempts.some(a => a.source === sourceFilter);
        if (!hasMatchingSource && leadAttempts.length > 0) return false;
      }
      if (dateRange) {
        const leadTime = lead.createdAt;
        if (leadTime < dateRange.from.getTime() || leadTime > dateRange.to.getTime()) return false;
      }
      if (inspectorSearch) {
        const q = inspectorSearch.toLowerCase();
        const profile = (lead.leadProfile || {}) as Record<string, unknown>;
        const matchesId = lead.id.toLowerCase().includes(q);
        const matchesType = lead.leadTypeName.toLowerCase().includes(q);
        const matchesAgent = (lead.assignedAgentName || "").toLowerCase().includes(q);
        const matchesState = (lead.state || "").toLowerCase().includes(q);
        const matchesPhone = String(profile.phone || "").toLowerCase().includes(q);
        const matchesName = `${String(profile.first_name || "")} ${String(profile.last_name || "")}`.toLowerCase().includes(q);
        if (!matchesId && !matchesType && !matchesAgent && !matchesState && !matchesPhone && !matchesName) return false;
      }
      return true;
    });
  }, [leads, statusFilter, sourceFilter, inspectorSearch, attemptsByLead, dateRange]);
  
  const toggleLead = (leadId: string) => {
    setExpandedLeads(prev => {
      const next = new Set(prev);
      if (next.has(leadId)) {
        next.delete(leadId);
      } else {
        next.add(leadId);
      }
      return next;
    });
  };
  
  const getOutcomeIcon = (status: LeadStatus) => {
    switch (status) {
      case "ROUTED": return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case "RETRY_QUEUED": return <RefreshCw className="h-4 w-4 text-blue-600" />;
      case "STOCKED": return <Package className="h-4 w-4 text-muted-foreground" />;
      case "UNSOLD": return <XCircle className="h-4 w-4 text-destructive" />;
      case "REVIEW_REQUIRED": return <AlertTriangle className="h-4 w-4 text-amber-600" />;
      default: return <AlertTriangle className="h-4 w-4 text-amber-500" />;
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
              <Eye className="h-6 w-6" />
              Routing Inspector
              {isLoading && (
                <span className="text-sm font-normal text-muted-foreground ml-2">Loading...</span>
              )}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Audit log of record • Lead lifecycle + routing attempt history
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Summary Stats (from DB) */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Database className="h-4 w-4 text-muted-foreground" />
                <span className="text-2xl font-bold">{leadStats?.total ?? 0}</span>
              </div>
              <p className="text-xs text-muted-foreground">Total Leads</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span className="text-2xl font-bold text-green-600">{leadStats?.byStatus.ROUTED ?? 0}</span>
              </div>
              <p className="text-xs text-muted-foreground">Routed</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4 text-blue-600" />
                <span className="text-2xl font-bold text-blue-600">{leadStats?.byStatus.RETRY_QUEUED ?? 0}</span>
              </div>
              <p className="text-xs text-muted-foreground">Retry Queue</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-muted-foreground" />
                <span className="text-2xl font-bold">{leadStats?.byStatus.STOCKED ?? 0}</span>
              </div>
              <p className="text-xs text-muted-foreground">Stocked</p>
            </CardContent>
          </Card>
          <Card className={(leadStats?.byStatus.REVIEW_REQUIRED ?? 0) > 0 ? 'border-amber-500/50' : ''}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className={`h-4 w-4 ${(leadStats?.byStatus.REVIEW_REQUIRED ?? 0) > 0 ? 'text-amber-600' : 'text-muted-foreground'}`} />
                <span className={`text-2xl font-bold ${(leadStats?.byStatus.REVIEW_REQUIRED ?? 0) > 0 ? 'text-amber-600' : ''}`}>
                  {leadStats?.byStatus.REVIEW_REQUIRED ?? 0}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">Needs Review</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <History className="h-4 w-4 text-purple-600" />
                <span className="text-2xl font-bold text-purple-600">{attemptStats?.total ?? 0}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Attempts ({((attemptStats?.successRate ?? 0) * 100).toFixed(0)}% success)
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Status:</span>
                <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="ROUTED">Routed</SelectItem>
                    <SelectItem value="RETRY_QUEUED">Retry Queued</SelectItem>
                    <SelectItem value="STOCKED">Stocked</SelectItem>
                    <SelectItem value="UNSOLD">Unsold</SelectItem>
                    <SelectItem value="REVIEW_REQUIRED">Needs Review</SelectItem>
                    <SelectItem value="NEW">New</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Source:</span>
                <Select value={sourceFilter} onValueChange={(v) => setSourceFilter(v as typeof sourceFilter)}>
                  <SelectTrigger className="w-[120px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="LIVE">Live</SelectItem>
                    <SelectItem value="RETRY">Retry</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[180px]">
                <Input
                  placeholder="Search name, phone, lead ID, type, agent, state..."
                  value={inspectorSearch}
                  onChange={(e) => setInspectorSearch(e.target.value)}
                  className="h-9"
                />
              </div>
              <Separator orientation="vertical" className="h-6" />
              <span className="text-sm text-muted-foreground">
                {filteredLeads.length} of {leads?.length ?? 0} leads
              </span>
            </div>
            <div className="mt-3">
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                Date Range
              </label>
              <DateRangeFilter
                value={dateRange}
                onChange={setDateRange}
                activePreset={datePreset}
                onPresetChange={setDatePreset}
              />
            </div>
          </CardContent>
        </Card>

        {/* Lead List with Routing Attempts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Lead History
            </CardTitle>
            <CardDescription>
              Click a lead to view its routing attempt timeline
            </CardDescription>
          </CardHeader>
          <CardContent>
            {filteredLeads.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Database className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">
                  {(leads?.length ?? 0) === 0 
                    ? "No leads yet. Go to Routing Lab to create a test lead."
                    : "No leads match the current filters."
                  }
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredLeads.map((lead) => {
                  const isExpanded = expandedLeads.has(lead.id);
                  const leadAttempts = attemptsByLead.get(lead.id) || [];
                  const lastAttempt = leadAttempts[0];
                  const isReviewRequired = lead.status === 'REVIEW_REQUIRED';
                  const validationErrors = lead.validationErrors as Array<{ field: string; message: string; code: string; severity: string }> | null;
                  const pricingRule = pricingActions.getPricingRuleForLead(lead.leadTypeId, lead.state);
                  const floorPrice = pricingRule && pricingRule.active ? pricingRule.basePrice : null;
                  const isBelowFloor = floorPrice !== null && lead.chargedAmount !== undefined && lead.chargedAmount < floorPrice;
                  
                  return (
                    <Collapsible key={lead.id} open={isExpanded} onOpenChange={() => toggleLead(lead.id)}>
                      <CollapsibleTrigger asChild>
                        <div className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-colors hover:bg-muted/50 ${isReviewRequired ? 'border-amber-500/50 bg-amber-500/5' : ''}`}>
                          <div className="flex items-center gap-2">
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                            {getOutcomeIcon(lead.status)}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{lead.leadTypeName}</span>
                              <Badge variant="outline" className="text-xs">{lead.state}</Badge>
                              <Badge 
                                variant={getStatusBadgeVariant(lead.status)} 
                                className={`text-xs ${isReviewRequired ? 'border-amber-500 text-amber-700 dark:text-amber-300' : ''}`}
                              >
                                {getStatusLabel(lead.status)}
                              </Badge>
                              {isReviewRequired && (
                                <span className="text-xs text-amber-600">Not routed automatically</span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {lead.id.substring(0, 8)}... • {new Date(lead.createdAt).toLocaleString()}
                            </p>
                          </div>
                          
                          <div className="text-right">
                            <div className="flex items-center gap-2 justify-end">
                              {lead.chargedAmount && (
                                <span className="text-sm font-medium text-green-600">
                                  ${lead.chargedAmount}
                                </span>
                              )}
                              {isBelowFloor && (
                                <Badge variant="destructive" className="text-xs">
                                  Below Floor
                                </Badge>
                              )}
                              {!isReviewRequired && (
                                <Badge variant="secondary" className="text-xs">
                                  {leadAttempts.length} attempt{leadAttempts.length !== 1 ? 's' : ''}
                                </Badge>
                              )}
                            </div>
                            {/* Last routing decision */}
                        {lastAttempt ? (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Last: <span className={
                              lastAttempt.result === 'ROUTED' ? 'text-green-600' :
                              lastAttempt.result === 'RETRY_QUEUED' ? 'text-blue-600' :
                              'text-destructive'
                            }>{lastAttempt.result}</span>
                            {lastAttempt.agentName && ` → ${lastAttempt.agentName}`}
                          </p>
                        ) : lead.assignedAgentName ? (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            → {lead.assignedAgentName}
                          </p>
                        ) : null}
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      
                      <CollapsibleContent>
                        <div className="ml-10 mt-2 space-y-2 pb-2">
                          {(
                            lead.chargedAmount !== undefined ||
                            floorPrice !== null ||
                            leadAttempts.length > 0
                          ) && (
                            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                              {lead.chargedAmount !== undefined && (
                                <Badge variant="outline" className="text-xs">
                                  Charged: ${lead.chargedAmount}
                                </Badge>
                              )}
                              {floorPrice !== null && (
                                <Badge variant={isBelowFloor ? "destructive" : "outline"} className="text-xs">
                                  Floor: ${floorPrice}
                                </Badge>
                              )}
                              {lastAttempt?.scoring?.priorityScore !== undefined && (
                                <Badge variant="outline" className="text-xs">
                                  Priority: {lastAttempt.scoring.priorityScore.toFixed(2)}
                                </Badge>
                              )}
                              {lastAttempt?.scoring?.qualityScore !== undefined && (
                                <Badge variant="outline" className="text-xs">
                                  Quality: {lastAttempt.scoring.qualityScore.toFixed(2)}
                                </Badge>
                              )}
                              {lastAttempt?.scoring?.bidScore !== undefined && lastAttempt.scoring.bidScore > 0 && (
                                <Badge variant="outline" className="text-xs">
                                  Bid: {lastAttempt.scoring.bidScore.toFixed(2)}
                                </Badge>
                              )}
                            </div>
                          )}
                          {/* Validation Errors (for REVIEW_REQUIRED leads) */}
                          {isReviewRequired && validationErrors && validationErrors.length > 0 && (
                            <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                              <p className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-2">
                                Validation Issues
                              </p>
                              <ul className="space-y-1">
                                {validationErrors.map((err, i) => (
                                  <li key={i} className="text-xs flex items-start gap-2">
                                    <Badge 
                                      variant={err.severity === 'HARD' ? 'destructive' : 'outline'} 
                                      className="text-xs shrink-0"
                                    >
                                      {err.severity}
                                    </Badge>
                                    <span>
                                      <strong>{err.field}:</strong> {err.message}
                                      {err.code && <span className="text-muted-foreground ml-1">({err.code})</span>}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {/* Review Actions Panel (for REVIEW_REQUIRED leads) */}
                          {isReviewRequired && (
                            <ReviewActionsPanel
                              leadId={lead.id}
                              currentLeadTypeId={lead.leadTypeId}
                              currentState={lead.state}
                              onSuccess={handleRefresh}
                            />
                          )}
                          
                          {/* Force Retry Now (DEV only for RETRY_QUEUED leads) */}
                          {lead.status === 'RETRY_QUEUED' && devInjectionAvailable && (
                            <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Bug className="h-4 w-4 text-purple-600" />
                                  <span className="text-sm font-medium text-purple-800 dark:text-purple-200">
                                    DEV: Force Retry
                                  </span>
                                </div>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-purple-500/50 text-purple-700 dark:text-purple-300"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleForceRetry(lead);
                                  }}
                                  disabled={retryingLeadId === lead.id}
                                >
                                  {retryingLeadId === lead.id ? (
                                    <>
                                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                      Retrying...
                                    </>
                                  ) : (
                                    <>
                                      <Zap className="h-3 w-3 mr-1" />
                                      Retry Now
                                    </>
                                  )}
                                </Button>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                Immediately retry with current DEV overrides applied.
                              </p>
                            </div>
                          )}
                          
                          {/* No Attempts Message */}
                          {leadAttempts.length === 0 && !isReviewRequired ? (
                            <p className="text-sm text-muted-foreground py-2 px-4 bg-muted/30 rounded">
                              No routing attempts recorded yet.
                            </p>
                          ) : leadAttempts.length === 0 && isReviewRequired ? (
                            <p className="text-sm text-muted-foreground py-2 px-4 bg-muted/30 rounded">
                              This lead was not routed automatically due to validation issues.
                            </p>
                          ) : (
                            leadAttempts.map((attempt) => (
                              <div key={attempt.id} className="p-3 bg-muted/30 rounded-lg border text-sm">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <Badge 
                                      variant={attempt.source === "LIVE" ? "default" : "secondary"}
                                      className="text-xs"
                                    >
                                      {attempt.source}
                                    </Badge>
                                    <span className="font-medium">
                                      Attempt #{attempt.attemptNumber}
                                    </span>
                                    <Badge 
                                      variant={attempt.result === "ROUTED" ? "default" : 
                                               attempt.result === "RETRY_QUEUED" ? "outline" : "destructive"}
                                      className="text-xs"
                                    >
                                      {attempt.result}
                                    </Badge>
                                    {attempt.devOverridesApplied && (
                                      <Badge variant="outline" className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-500/50">
                                        DEV
                                      </Badge>
                                    )}
                                    {/* Show DEV_INJECTED badge based on pool size - indicates agents were injected */}
                                    {attempt.devOverridesApplied && attempt.eligibleAgentsCount > 0 && (
                                      <Badge variant="outline" className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-500/50">
                                        DEV_INJECTED
                                      </Badge>
                                    )}
                                  </div>
                                  <span className="text-xs text-muted-foreground">
                                    {attempt.durationMs}ms • {new Date(attempt.createdAt).toLocaleTimeString()}
                                  </span>
                                </div>
                                
                                <div className="mt-2 grid grid-cols-4 gap-4 text-xs">
                                  <div className="flex items-center gap-1">
                                    <User className="h-3 w-3 text-muted-foreground" />
                                    <span>{attempt.agentName || 'None'}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <DollarSign className="h-3 w-3 text-muted-foreground" />
                                    <span>{attempt.chargedAmount ? `$${attempt.chargedAmount}` : 'N/A'}</span>
                                  </div>
                                  <div>
                                    <span className="text-muted-foreground">Pool:</span>{' '}
                                    <span className={attempt.devOverridesApplied && attempt.eligibleAgentsCount > 0 ? 'text-amber-600 font-medium' : ''}>
                                      {attempt.eligibleAgentsCount}/{attempt.totalAgentsEvaluated}
                                    </span>
                                    {attempt.devOverridesApplied && attempt.eligibleAgentsCount > 0 && (
                                      <span className="text-xs text-amber-600 ml-1">(injected)</span>
                                    )}
                                  </div>
                                  <div>
                                    {attempt.guardrailsApplied && (
                                      <Badge variant="outline" className="text-xs">
                                        Guardrails
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                                
                                {attempt.rejectionCode && (
                                  <div className="mt-2 text-xs text-destructive">
                                    {attempt.rejectionCode}: {attempt.rejectionReason}
                                  </div>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Debug Panel: Session Event Store (collapsible) - NOT AUTHORITATIVE */}
        {eventStats.total > 0 && (
          <Collapsible open={debugPanelOpen} onOpenChange={setDebugPanelOpen}>
            <Card className="border-dashed border-amber-500/30 bg-amber-500/5 opacity-75">
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-amber-500/10 transition-colors">
                  <CardTitle className="text-sm flex items-center gap-2">
                    {debugPanelOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    <Activity className="h-4 w-4 text-amber-600" />
                    Session Debug Only
                    <Badge variant="outline" className="ml-2 text-xs bg-amber-100 dark:bg-amber-900/30">
                      {eventStats.total} in-memory
                    </Badge>
                    <Badge variant="destructive" className="text-xs">NOT AUTHORITATIVE</Badge>
                  </CardTitle>
                  <CardDescription>
                    ⚠️ This is session-scoped memory only. For audit records, use the database-backed Lead History above.
                  </CardDescription>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent>
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <span className="text-muted-foreground">Sim:</span>
                      <Badge variant="secondary">{eventStats.bySource.SIMULATION}</Badge>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-muted-foreground">Live:</span>
                      <Badge variant="default">{eventStats.bySource.LIVE}</Badge>
                    </div>
                    <Separator orientation="vertical" className="h-4" />
                    <div className="flex items-center gap-1">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <span>{eventStats.byOutcome.ROUTED}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <RefreshCw className="h-4 w-4 text-blue-600" />
                      <span>{eventStats.byOutcome.RETRY_QUEUED}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <XCircle className="h-4 w-4 text-destructive" />
                      <span>{eventStats.byOutcome.UNSOLD}</span>
                    </div>
                    <Separator orientation="vertical" className="h-4" />
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={(e) => {
                        e.stopPropagation();
                        eventActions.clearAll();
                      }}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Clear
                    </Button>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        )}
      </div>
    </AdminLayout>
  );
}
